import React, { type ReactNode } from 'react';
import ChevronRight from '@atlaskit/icon/core/chevron-right';
import ChevronLeft from '@atlaskit/icon/core/chevron-left';

import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { SIDEBAR_RESIZE_BUTTON_SELECTOR } from '../../../common/constants.tsx';
import { messages } from './messages.tsx';
import {
	ExpandedHitArea,
	ResizeBoundaryButton,
	ResizeBoundaryGrabArea,
	StyledResizeButton,
	TooltipNode,
} from './styled.tsx';

type TooltipProps = {
	isSidebarCollapsed: boolean;
	children: ReactNode;
};

type ResizeControlProps = {
	isSidebarCollapsed: boolean;
	onClick: () => void;
};

// This is mostly copied from src/packages/page-container-v2/src/common/ui/sidebar/resize-button-tooltip/main.tsx
export const ResizeButtonTooltip = ({
	isSidebarCollapsed,
	children,
}: TooltipProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const text = isSidebarCollapsed
		? formatMessage(messages.expand)
		: formatMessage(messages.collapse);

	return (
		<Tooltip content={<TooltipNode text={text} char="]" />} hideTooltipOnClick position="left">
			{children}
		</Tooltip>
	);
};

export const ResizeButton = ({
	isSidebarCollapsed,
	onClick,
}: ResizeControlProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<ResizeButtonTooltip isSidebarCollapsed={isSidebarCollapsed}>
			<StyledResizeButton
				aria-expanded={isSidebarCollapsed}
				aria-label={formatMessage(messages.resizeButtonLabel)}
				data-component-selector={SIDEBAR_RESIZE_BUTTON_SELECTOR}
				isSidebarCollapsed={isSidebarCollapsed}
				onClick={onClick}
				onMouseDown={(event) => event.preventDefault()}
			>
				{isSidebarCollapsed ? (
					<ChevronLeft spacing="spacious" label="" size="small" />
				) : (
					<ChevronRight spacing="spacious" label="" size="small" />
				)}
				<ExpandedHitArea />
			</StyledResizeButton>
		</ResizeButtonTooltip>
	);
};

export const ResizeBoundary = ({
	isSidebarCollapsed,
	onClick,
}: ResizeControlProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<ResizeBoundaryButton
			aria-expanded={isSidebarCollapsed}
			aria-label={formatMessage(messages.resizeBoundaryLabel)}
			onClick={onClick}
			onMouseDown={(event) => event.preventDefault()}
		>
			<ResizeBoundaryGrabArea />
		</ResizeBoundaryButton>
	);
};
